import posthog from "posthog-js";
import { metaConversion, startMetaPixel, stopMetaPixel } from "./meta-pixel";

/*
  CYKL ZYCIA ANALITYKI — jedyne miejsce w aplikacji, ktore dotyka posthog-js.

  Pelna analityka (cookies PostHoga, tozsamosc, Meta Pixel) rusza DOPIERO po zgodzie na cookies
  i zatrzymuje sie po jej cofnieciu. Bez zgody — przed decyzja i po odmowie — PostHog liczy
  anonimowo w trybie bez cookies (`cookieless_mode: 'on_reject'`): niczego nie zapisuje na
  urzadzeniu, a odwiedzajacego rozpoznaje dziennym hashem liczonym po stronie serwera. Wymaga
  wlaczonego "Cookieless server hash mode" w ustawieniach projektu PostHog — bez tego serwer
  takie zdarzenia odrzuca. Podstawa: uzasadniony interes (art. 6 ust. 1 lit. f RODO), polityka
  prywatnosci par. 2.1.7. Bez tego ruch z reklam byl niewidoczny: 2026-10-20 dziewiec wejsc
  z kampanii, zero w PostHogu, bo nikt nie kliknal "Akceptuje".
  Wczesniej `posthog.init` stal w main.tsx i wykonywal sie przy kazdym zaladowaniu strony,
  zanim uzytkownik cokolwiek kliknal — a wybor z banera byl czytany tylko po to, zeby ten
  baner pokazac albo ukryc. "Odrzuc" nie zmienialo nic (TASK-82).

  Podstawa nie jest wygoda, tylko prawo: art. 399 Prawa komunikacji elektronicznej wymaga
  UPRZEDNIEJ zgody na zapis/odczyt na urzadzeniu poza tym, co niezbedne do dzialania uslugi
  (analityka niezbedna nie jest), a polityka prywatnosci deklaruje dla analityki zgode
  (art. 6 ust. 1 lit. a RODO) — bez niej nie ma podstawy przetwarzania.
*/

const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

/**
 * Czy wolno WYSLAC cokolwiek do PostHoga. Zgoda to warunek konieczny, nie wystarczajacy:
 * w trybie deweloperskim nie zasmiecamy prawdziwego projektu zdarzeniami z localhosta,
 * a bez skonfigurowanego klucza nie ma dokad wysylac.
 */
const canSendToPostHog = () => Boolean(POSTHOG_KEY && POSTHOG_HOST) && !import.meta.env.DEV;

let analyticsActive = false;

/*
  Tozsamosc zalogowanej osoby. Trzymana niezaleznie od zgody, bo logowanie zwykle wyprzedza
  klikniecie w banerze: identify musi zadzialac takze wtedy, gdy analityka ruszy pozniej.
  Bez identify PostHog nie laczy wizyt tej samej osoby — nie da sie zmierzyc ani lejka
  (anonimowe "Generuj" -> konto), ani powrotow (TASK-101.1).
*/
let identifiedUserId: string | null = null;

/** Czy pelna analityka jest wlaczona, tzn. uzytkownik wyrazil zgode. */
export const isAnalyticsActive = () => analyticsActive;

/*
  Uchwyt dla testow e2e. Sama DECYZJA (zgoda -> analityka wlaczona) jest tym, co ten ticket
  naprawia, i musi dac sie sprawdzic takze w trybie deweloperskim, gdzie wysylki do PostHoga
  celowo nie ma — bez tego test przechodzilby dlatego, ze nic sie nie dzieje, a nie dlatego,
  ze zgoda dziala.
*/
type AnalyticsTestState = {
  __analyticsActive?: boolean;
  __analyticsMode?: 'cookieless' | 'full';
  __analyticsUserId?: string | null;
  __analyticsEvents?: { event: string; props: Record<string, unknown> }[];
};
const testState = () => window as unknown as AnalyticsTestState;

const publishStateForTests = () => {
  testState().__analyticsActive = analyticsActive;
  testState().__analyticsMode = analyticsActive ? 'full' : 'cookieless';
  testState().__analyticsUserId = identifiedUserId;
};

/** Kasuje cookies PostHoga (ph_<klucz>_posthog i pochodne) z biezacej domeny oraz jego znacznik okna z sessionStorage. */
function clearPostHogStorage() {
  for (const entry of document.cookie.split(";")) {
    const name = entry.split("=")[0]?.trim();
    if (!name?.startsWith("ph_")) continue;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/`;
  }
  // reset() nie czysci ph_<klucz>_window_id — sprawdzone na buildzie produkcyjnym.
  for (const key of Object.keys(sessionStorage)) if (key.startsWith("ph_")) sessionStorage.removeItem(key);
}

/** PostHog startuje zawsze — domyslnie wylaczony, czyli w trybie bez cookies. Zgoda go wlacza. */
function initPostHog() {
  if (!canSendToPostHog() || posthog.__loaded) return;
  posthog.init(POSTHOG_KEY!, {
    api_host: POSTHOG_HOST!,
    cookieless_mode: 'on_reject',
    opt_out_capturing_by_default: true,
  });
}

function startAnalytics() {
  if (analyticsActive) return;
  analyticsActive = true;
  publishStateForTests();
  startMetaPixel();
  if (!posthog.__loaded) return;
  posthog.opt_in_capturing();
  if (identifiedUserId) posthog.identify(identifiedUserId);
}

function stopAnalytics() {
  analyticsActive = false;
  publishStateForTests();
  stopMetaPixel();
  // Sprzatamy TAKZE wtedy, gdy analityka nie byla aktywna w tej sesji: uzytkownik mogl
  // zaakceptowac cookies wczesniej, a teraz zgode cofnac — jego cookies maja zniknac.
  // opt_out przy cookieless_mode 'on_reject' nie wylacza liczenia, tylko wraca do trybu bez cookies.
  if (posthog.__loaded && posthog.has_opted_in_capturing()) {
    posthog.opt_out_capturing();
    posthog.reset(true);
  }
  clearPostHogStorage();
}

/**
 * Jedyne wejscie do wlaczania/wylaczania analityki. Wolane przez privacyService przy KAZDYM
 * zapisie zgody oraz raz na starcie aplikacji — dzieki temu nie da sie zapisac decyzji
 * uzytkownika i zapomniec o jej skutku.
 */
export function identifyAnalyticsUser(userId: string | null): void {
  const previous = identifiedUserId;
  identifiedUserId = userId;
  publishStateForTests();
  if (!analyticsActive || !posthog.__loaded || previous === userId) return;
  if (userId) posthog.identify(userId);
  // Wylogowanie: kolejna osoba przy tym samym komputerze (szkola) nie moze dostac cudzej tozsamosci.
  else posthog.reset();
}

export function applyAnalyticsConsent(status: "accepted" | "rejected" | null): void {
  initPostHog();
  if (status === "accepted") startAnalytics();
  else stopAnalytics();
}


/*
  Zdarzenia produktowe eksperymentu z kampania (TASK-101). Mierza lejek (Generuj -> konto ->
  lekcja gotowa) i to, czy lekcje UZYTO, a nie tylko wygenerowano: prezentacja, PPTX, druk,
  udostepnienie, widok ucznia. Retencje liczymy z bazy (niezalezna od zgody na cookies);
  PostHog sluzy do lejka i zachowan.
*/
type AnalyticsEvents = {
  generate_clicked: { authenticated: boolean };
  account_created: { method: string };
  /** sam scenariusz nowej lekcji gotowy — wczesniej niz lesson_ready (ten czeka na cala autogeneracje) */
  scenario_ready: { duration_s: number };
  lesson_ready: { duration_s: number; education_level?: string; grade?: number; lesson_minutes?: number };
  lesson_modified: { section: 'scenario' | 'knowledge' | 'slides'; has_instruction: boolean };
  slides_presented: Record<string, never>;
  pptx_downloaded: Record<string, never>;
  lesson_printed: Record<string, never>;
  share_link_copied: { link: 'teacher' | 'student' };
  student_view_opened: { owned: boolean };
  credits_exhausted: Record<string, never>;
  purchase_credits: { amount: number; currency: string; success: boolean };
  topup_clicked: { exhausted: boolean };
  /** gosc na cudzej publicznej lekcji (np. z reklamy) klika "Stworz wlasna lekcje" */
  create_own_lesson_clicked: Record<string, never>;
};

export function track<E extends keyof AnalyticsEvents>(
  event: E,
  ...[props]: AnalyticsEvents[E] extends Record<string, never> ? [] : [AnalyticsEvents[E]]
): void {
  // Bez zgody zdarzenie idzie anonimowo (PostHog bez cookies); Meta sama pilnuje zgody.
  const defined = Object.fromEntries(Object.entries(props ?? {}).filter(([, value]) => value !== undefined));
  // Ten sam uchwyt co __analyticsActive: e2e sprawdza, ze AKCJA wysyla zdarzenie, takze na devie,
  // gdzie do PostHoga celowo nic nie idzie.
  (testState().__analyticsEvents ??= []).push({ event, props: defined });
  if (posthog.__loaded) posthog.capture(event, defined);
  metaConversion(event);
}
